import type { sortSpecSchema } from '@smartsash/data-screen';
import type { FilterCondition } from '@smartsash/ui';
import type { z } from 'zod';

type SortSpec = z.infer<typeof sortSpecSchema>;

/**
 * How one builder field reaches the query string of a list endpoint.
 *
 * `multi` is the shape most scope filters take: the endpoint accepts the parameter repeatedly and
 * ORs the repetitions, so a checkbox list maps onto it directly. `boolean` is a single-valued flag
 * (`online`, `passed`, `isEnabled`), and `dateRange` is the pair of bounds the endpoints spell as
 * two separate parameters rather than as one range. `single` is one scalar value into one scalar
 * parameter — the shape a per-field text or single-choice condition takes when the endpoint has no
 * repeatable or ranged form for it. `dateBound` is a single upper bound rather than a pair: some
 * endpoints take one `…Before`-style parameter instead of the `dateRange` pair.
 *
 * The parameter names are constrained to the keys of the endpoint's generated params type. The
 * endpoints answer `400 Bad Request` to a parameter they do not know, so a typo here would be a
 * blank screen at runtime; this makes it a compile error instead.
 */
export type FilterParamSpec<TParams> =
  | { kind: 'multi'; param: keyof TParams & string }
  | { kind: 'boolean'; param: keyof TParams & string }
  | { kind: 'dateRange'; from: keyof TParams & string; to: keyof TParams & string }
  | { kind: 'single'; param: keyof TParams & string }
  | { kind: 'dateBound'; param: keyof TParams & string };

/**
 * A page's declaration of which of its builder fields its endpoint can actually receive.
 *
 * Partial, because the builder offers every column as a field while only some of them have a
 * parameter behind them — looking one up has to be allowed to miss.
 */
export type FilterParamMap<TParams> = Readonly<Partial<Record<string, FilterParamSpec<TParams>>>>;

/** The one value a boolean filter can mean; anything else is an incoherent selection. */
const SINGLE = 1;

/** A bare `YYYY-MM-DD` date, with nothing else trailing it, captured into its three parts. */
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The last instant of a day in UTC, as the trailing arguments `Date.UTC` takes. */
const END_OF_DAY_UTC = { hours: 23, minutes: 59, seconds: 59, milliseconds: 0 } as const;

/** January is month `0` in `Date.UTC`, so a human month has to shift by this before it goes in. */
const MONTH_INDEX_OFFSET = 1;

/**
 * Normalise a `YYYY-MM-DD` date bound to the end of that day in UTC.
 *
 * A `dateBound` parameter compares against a timestamp, not a calendar day, so sending the bare
 * date would be read as midnight and would silently exclude every record that falls anywhere
 * later on the boundary day itself. Anchoring to the day's last second keeps the whole boundary
 * day inside the bound.
 *
 * Returns `undefined` for anything that is not a real calendar date, so an invalid or empty value
 * contributes nothing rather than a malformed parameter. `Date.UTC` does not reject an
 * out-of-range day on its own — `2026-02-30` quietly rolls forward into March — so the parsed
 * parts are read back out of the built date and compared against what went in; only a genuine
 * calendar date round-trips.
 */
function endOfDayUtc(value: string): string | undefined {
  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) return undefined;
  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);

  const date = new Date(
    Date.UTC(
      year,
      month - MONTH_INDEX_OFFSET,
      day,
      END_OF_DAY_UTC.hours,
      END_OF_DAY_UTC.minutes,
      END_OF_DAY_UTC.seconds,
      END_OF_DAY_UTC.milliseconds,
    ),
  );
  const roundTrips =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - MONTH_INDEX_OFFSET &&
    date.getUTCDate() === day;
  return roundTrips ? date.toISOString() : undefined;
}

/**
 * Turn the applied filter set into the query parameters an endpoint accepts.
 *
 * Only fields the page declared in `map` reach the query. An undeclared field contributes nothing:
 * narrowing it on the client would shrink the rows on screen without shrinking the result set, so
 * the count badge and the pager would then disagree with the table under them.
 *
 * A `contains` condition usually has nowhere to land here — most endpoints have no per-field
 * substring parameter, only the cross-field `search`, and `collectSearchTerms` handles those. The
 * one exception is `single`, whose endpoint matches its own scalar parameter on a substring, so a
 * `contains` row on a `single` field reaches its own parameter directly instead of `search`.
 */
export function buildFilterParams<TParams>(
  conditions: FilterCondition[],
  map: FilterParamMap<TParams>,
): Partial<TParams> {
  const params: Record<string, unknown> = {};

  for (const condition of conditions) {
    const spec = map[condition.field];
    if (!spec) continue;

    if (spec.kind === 'multi' && condition.operator === 'eq') {
      const values = condition.values.filter((value) => value !== '');
      if (values.length === 0) continue;
      const carried = params[spec.param];
      // Two rows on the same field are two halves of one OR, so they merge rather than the second
      // replacing the first. A repeated value would ask the server the same thing twice.
      const existing = Array.isArray(carried) ? (carried as string[]) : [];
      params[spec.param] = [...new Set([...existing, ...values])];
      continue;
    }

    if (spec.kind === 'boolean' && condition.operator === 'eq') {
      // Checking both `true` and `false` asks for everything, which is what sending no parameter
      // already means — and the endpoint takes one boolean, not a list of them.
      if (condition.values.length !== SINGLE) continue;
      params[spec.param] = condition.values[0] === 'true';
      continue;
    }

    if (spec.kind === 'dateRange' && condition.operator === 'between') {
      const [from = '', to = ''] = condition.values;
      if (from !== '') params[spec.from] = from;
      if (to !== '') params[spec.to] = to;
      continue;
    }

    if (
      spec.kind === 'single' &&
      (condition.operator === 'eq' || condition.operator === 'contains')
    ) {
      const [value = ''] = condition.values;
      if (value === '') continue;
      // The parameter is scalar, so two rows on the same field cannot both reach it — unlike
      // `multi`, there is no OR to merge into. The later row simply overwrites the earlier one.
      params[spec.param] = value;
      continue;
    }

    if (spec.kind === 'dateBound' && condition.operator === 'lte') {
      const [value = ''] = condition.values;
      const bound = endOfDayUtc(value);
      if (bound === undefined) continue;
      params[spec.param] = bound;
    }
  }

  // The map's parameter names are keys of `TParams`, and each branch above writes the shape that
  // key holds, which is what makes this the one cast rather than a claim about arbitrary data.
  return params as Partial<TParams>;
}

/**
 * Turn the URL's `sort` into the `sortBy`/`sortDir` pair a list endpoint accepts.
 *
 * `sort.field` is the table's own column id, which the endpoint's `sortBy` may or may not share the
 * spelling of — `sortKeys` is the page's declared column id → API field map, the same shape
 * `useListSort` reads for the pages that render `Table` by hand. A column with no entry (or no
 * applied sort at all) contributes nothing, which leaves the query on whatever order the endpoint
 * defaults to rather than sending a parameter it would answer `400` to.
 */
export function sortParams<TParams extends { sortBy?: string; sortDir?: string }>(
  sort: SortSpec | undefined,
  sortKeys: Readonly<Record<string, string>>,
): Pick<TParams, 'sortBy' | 'sortDir'> | Record<string, never> {
  if (!sort || !(sort.field in sortKeys)) return {};
  return { sortBy: sortKeys[sort.field], sortDir: sort.dir };
}

/**
 * The terms that go into the endpoint's `search` parameter.
 *
 * `search` is the only free-text predicate any list endpoint has, and it is deliberately
 * cross-field: the server matches a term against a set of columns fixed per endpoint, and
 * repeating the parameter ANDs the terms. So a `contains` row does narrow the result set, but it
 * narrows it by "this text appears somewhere in the row" rather than by "this text appears in this
 * column". The builder cannot promise more than the API can express, and approximating a per-field
 * substring on the client would break pagination.
 *
 * The table's own search box contributes one more term, which is why both sources are collected
 * here rather than at each call site.
 *
 * `map`, when given, is the same `FilterParamMap` the page passes to `buildFilterParams`. A
 * `contains` condition whose field has a `single` spec in that map already reaches its own scalar
 * parameter, so folding it into `search` as well would AND a second, broader predicate onto the
 * same query — "matches this column" and "matches this column or these others" are not the same
 * request. Only `single` is skipped, and deliberately so: it is the one kind that consumes a
 * `contains`. A `contains` on a `multi` or `dateRange` field reaches no parameter at all, so
 * dropping it here as well would make the condition vanish rather than degrade to a broader
 * search. Omitting `map` keeps every existing call site's behaviour exactly as it was before this
 * parameter existed.
 */
export function collectSearchTerms<TParams>(
  conditions: FilterCondition[],
  searchText: string,
  map?: FilterParamMap<TParams>,
): string[] | undefined {
  const terms = conditions
    .filter((condition) => condition.operator === 'contains')
    .filter((condition) => map?.[condition.field]?.kind !== 'single')
    .flatMap((condition) => condition.values)
    .concat(searchText)
    .map((term) => term.trim())
    .filter((term) => term !== '');

  const unique = [...new Set(terms)];
  return unique.length > 0 ? unique : undefined;
}
